import { Bounty, BountyProposal, BountyCategory, BountyClaim, BountyClaimStatus, BountyIntroChannel, InfluencerLocation, normalizeBountyCompanyName, getBountyLocationPoints } from '@foodfluence/shared';
import { calculateDistance, hasValidCoordinates } from '../../utils/locationUtils';
import { HIGH_PAYING_THRESHOLD, NEW_BADGE_DAYS } from './constants';
import { CATEGORY_OPTIONS } from './proposalFormUtils';

/**
 * Bounty timestamps arrive in two shapes: `{seconds}` from the client Firestore SDK
 * and `{_seconds}` from admin-SDK objects serialized through the Express API.
 */
export function bountyTimestampToMillis(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { seconds?: number; _seconds?: number };
  const seconds = v.seconds ?? v._seconds;
  if (typeof seconds !== 'number') return null;
  return seconds * 1000;
}

export function isNewBounty(bounty: Bounty, nowMs: number): boolean {
  const created = bountyTimestampToMillis(bounty.createdTime);
  if (created === null) return false;
  return nowMs - created < NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
}

export function isHighPaying(bounty: Bounty): boolean {
  return (bounty.amount ?? 0) >= HIGH_PAYING_THRESHOLD;
}

export function claimStatusChip(status: BountyClaimStatus): { label: string; colorScheme: string } {
  switch (status) {
    case 'claimed': return { label: 'Claimed', colorScheme: 'gray' };
    case 'introSubmitted': return { label: 'Intro Submitted', colorScheme: 'blue' };
    case 'verified': return { label: 'Verified', colorScheme: 'green' };
    case 'notSelected': return { label: 'Not Selected', colorScheme: 'gray' };
    case 'introRejected': return { label: 'Needs New Contact', colorScheme: 'orange' };
  }
}

export function claimPayoutLabel(claim: BountyClaim): string {
  return claim.payoutStatus === 'paid' ? 'Paid' : 'Open Wallet';
}

export function proposalStatusChip(
  proposal: Pick<BountyProposal, 'status' | 'rejectionNote'> | { status: 'approved'; amount?: number },
): { label: string; colorScheme: string } {
  if (proposal.status === 'pendingReview') return { label: 'Under Review', colorScheme: 'blue' };
  if (proposal.status === 'rejected') return { label: 'Not Approved', colorScheme: 'gray' };
  const amount = 'amount' in proposal ? proposal.amount : undefined;
  return { label: `Approved — $${amount ?? 0}`, colorScheme: 'green' };
}

export function proposalSubmittedPath(proposalId: string): string {
  return `/bounties/proposals/${proposalId}/submitted`;
}

export function resubmitProposalPath(proposalId: string): string {
  return `/bounties/proposals/${proposalId}/resubmit`;
}

export const myBountiesPath = '/bounties?tab=mine';

export function resubmitIntroPath(claimId: string): string {
  return `/bounties/claims/${claimId}/resubmit`;
}

export function claimRoutePath(claim: BountyClaim): string {
  switch (claim.status) {
    case 'claimed': return `/bounties/claims/${claim.id}/claimed`;
    case 'introRejected': return resubmitIntroPath(claim.id);
    default: return `/bounties/claims/${claim.id}/submitted`;
  }
}

/** True when the bounty's company matches one of the creator's Brandfetch-derived worked-with brands. */
export function isRecommended(bounty: Bounty, brands: string[]): boolean {
  if (brands.length === 0) return false;
  const normalizedCompany = normalizeBountyCompanyName(bounty.companyName);
  return brands.some(brand => normalizeBountyCompanyName(brand) === normalizedCompany);
}

/**
 * Minimum distance (miles) from the bounty to any of the creator's locations, but only when
 * that distance is within that location's own maxCollabDistance range. Returns null when the
 * bounty is national, missing valid coords, or out of range of every location.
 */
export function nearMeDistanceMiles(bounty: Bounty, locations: InfluencerLocation[]): number | null {
  if (bounty.isNational) return null;
  const bountyPoints = getBountyLocationPoints(bounty);
  if (bountyPoints.length === 0) return null;

  let min: number | null = null;
  for (const loc of locations) {
    if (!hasValidCoordinates(loc)) continue;
    const range = loc.maxCollabDistance ?? 25;
    for (const point of bountyPoints) {
      if (!hasValidCoordinates({ lat: point.lat, lng: point.lng } as InfluencerLocation)) continue;
      const distance = calculateDistance(loc.lat, loc.lng, point.lat, point.lng);
      if (distance <= range && (min === null || distance < min)) min = distance;
    }
  }
  return min;
}

/** True when the bounty falls within range of at least one of the creator's valid locations. */
export function isNearMe(bounty: Bounty, locations: InfluencerLocation[]): boolean {
  return nearMeDistanceMiles(bounty, locations) !== null;
}

/**
 * Board ordering for the default "All" view: invited bounties pin above recommended,
 * which pin above the rest. Stable within each tier (relies on Array.prototype.sort's
 * spec-guaranteed stability), so callers control intra-tier order by list order in.
 */
export function sortBoardBounties(bounties: Bounty[], invitedIds: string[], recommendedIds: string[]): Bounty[] {
  const invited = new Set(invitedIds);
  const recommended = new Set(recommendedIds);
  const tier = (bounty: Bounty): number => {
    if (invited.has(bounty.id)) return 0;
    if (recommended.has(bounty.id)) return 1;
    return 2;
  };
  return [...bounties].sort((a, b) => tier(a) - tier(b));
}

/** Board chip selection: one category at a time, with "High paying" living alongside the categories. */
export type BoardCategoryFilter = 'all' | BountyCategory | 'highPaying';

export type BoardFilterOptions = {
  search: string;
  category: BoardCategoryFilter;
  /** Independent toggle — stacks with whichever category is selected. */
  nearMe: boolean;
  locations: InfluencerLocation[];
  invitedIds: string[];
  recommendedIds: string[];
};

/** Live board search: company name only, case-insensitive, whitespace-tolerant. */
export function matchesBountySearch(bounty: Bounty, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return bounty.companyName.toLowerCase().includes(query);
}

export function matchesBoardCategory(bounty: Bounty, category: BoardCategoryFilter): boolean {
  if (category === 'all') return true;
  if (category === 'highPaying') return isHighPaying(bounty);
  return bounty.category === category;
}

/**
 * The board's whole filter + ordering pipeline: search, then category, then the
 * independent Near me toggle. Near me sorts by distance (closest first); every other
 * view keeps the invited > recommended > rest tiering.
 */
export function filterBoardBounties(bounties: Bounty[], options: BoardFilterOptions): Bounty[] {
  const matched = bounties.filter(
    b => matchesBountySearch(b, options.search) && matchesBoardCategory(b, options.category)
  );

  if (options.nearMe) {
    return matched
      .map(bounty => ({ bounty, distance: nearMeDistanceMiles(bounty, options.locations) }))
      .filter((entry): entry is { bounty: Bounty; distance: number } => entry.distance !== null)
      .sort((a, b) => a.distance - b.distance)
      .map(entry => entry.bounty);
  }

  return sortBoardBounties(matched, options.invitedIds, options.recommendedIds);
}

/**
 * Copy for the board's zero-result state — filters hiding everything reads differently
 * from a board that has nothing on it yet.
 */
export function boardEmptyCopy(activeBountyCount: number): { title: string; body: string } {
  if (activeBountyCount > 0) {
    return {
      title: 'No matches',
      body: 'Try a different filter, or submit the company you have in mind as a referral.',
    };
  }
  return { title: 'No bounties yet', body: 'Check back soon or submit your own referral.' };
}

/**
 * Joins brand names into a natural-language list, naming at most the first two and
 * summarizing the rest, e.g. "Saie, Glossier, and 3 more". Takes the full list (not a
 * pre-sliced display subset) so the "more" count reflects the true remainder.
 */
export function formatBrandList(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]}, and ${names.length - 2} more`;
}

/**
 * The intro email creators send to their brand contact (sales-approved wording). The
 * [name] placeholder stays literal — we don't know the contact's name; creators fill
 * it in themselves. Used as both the mailto body and the copyable template.
 */
export function introEmailTemplate(salesFirstName: string): string {
  return (
    'Hi [name],\n\n' +
    'Loved working with you guys!\n\n' +
    'I wanted to introduce you to Foodfluence, a platform that helps brands work with creators ' +
    'more consistently and get a steady flow of social content without needing a huge budget.\n\n' +
    `I've cc'd ${salesFirstName}, who leads partnerships there.\n\n` +
    'Thought it could be helpful for you based on the collaborations we\'ve done.\n\n' +
    `I'll let ${salesFirstName} jump in from here.`
  );
}

/**
 * Short intro for the chat channels (text, WhatsApp, Instagram DM), in the same voice as the
 * email. [name] stays literal for the creator to fill in. Email returns the email template.
 */
export function introMessageTemplate(channel: BountyIntroChannel, salesFirstName: string): string {
  if (channel === 'email') return introEmailTemplate(salesFirstName);
  const pitch =
    'Hi [name]! Loved working with you guys. I wanted to introduce you to Foodfluence, a platform ' +
    'that helps brands work with creators more consistently and get a steady flow of social content ' +
    'without needing a huge budget.';
  if (channel === 'dm') {
    return `${pitch}\n\nWould you be open to a quick chat with ${salesFirstName}, who leads partnerships there? Happy to connect you two.`;
  }
  const where = channel === 'whatsapp' ? 'this chat' : 'this text';
  return `${pitch}\n\nI've added ${salesFirstName}, who leads partnerships there, to ${where}. I'll let ${salesFirstName} jump in from here.`;
}

/** Card meta line, e.g. "Restaurants · 6 locations" or "CPG Brands · Austin". */
export function bountyMetaLine(bounty: Pick<Bounty, 'category' | 'locations' | 'isNational'>): string {
  const category = CATEGORY_OPTIONS.find(o => o.value === bounty.category)?.label;
  const locations = bounty.locations ?? [];
  const place = bounty.isNational
    ? 'National'
    : locations.length === 1
      ? locations[0].city
      : locations.length > 1
        ? `${locations.length} locations`
        : undefined;
  return [category, place].filter(Boolean).join(' · ');
}

/** Approved creator proposals that became private bounties the creator hasn't claimed yet. */
export function unclaimedApprovedBounties(bounties: Bounty[], claims: Pick<BountyClaim, 'bountyID'>[]): Bounty[] {
  const claimed = new Set(claims.map(c => c.bountyID));
  return bounties.filter(
    b => b.source === 'creatorProposal' && b.status === 'active' && b.visibility === 'private' && !claimed.has(b.id),
  );
}

/** My Bounties tab badge: claims waiting on the creator plus approved bounties ready to claim. */
export function myBountiesActionCount(bounties: Bounty[], claims: BountyClaim[]): number {
  const waiting = claims.filter(c => c.status === 'claimed' || c.status === 'introRejected').length;
  return waiting + unclaimedApprovedBounties(bounties, claims).length;
}
